import { create } from "zustand";
import { persist } from "zustand/middleware";
import { decodeJwt } from "@/lib/rbac";

// Auth store — JWT bearer token + user identity, persisted to localStorage.
// On boot, if the stored token's expires_at is in the past we wipe it.
// The JWT payload (role, client_ids) is *decoded from the token itself*, so it
// stays trustworthy even if the login response body omits either field.
//
// Server is still authoritative — we only decode to shape the UI.
export const useAuth = create(
  persist(
    (set, get) => ({
      token: "",
      username: "",
      role: "",
      clientIds: [], // string[] — operator scope
      expires_at: 0, // unix ms

      isAuthenticated: () => {
        const { token, expires_at } = get();
        if (!token) return false;
        if (expires_at && expires_at < Date.now()) return false;
        return true;
      },

      /**
       * setSession
       * Prefers claims decoded from the JWT itself. Falls back to the values
       * from the /api/auth/login response body if they aren't in the token.
       */
      setSession: ({ token, user, expires_at }) => {
        const claims = decodeJwt(token) || {};
        // Support several claim shapes the backend might emit.
        const role = claims.role || user?.role || "";
        const username = claims.username || claims.sub || user?.username || "";
        const rawScope =
          claims.client_ids ??
          claims.clientIds ??
          claims.scope_client_ids ??
          user?.client_ids ??
          [];
        const clientIds = Array.isArray(rawScope) ? rawScope.map(String) : [];
        // Prefer JWT exp (seconds) when present.
        const exp =
          typeof claims.exp === "number"
            ? claims.exp * 1000
            : expires_at || 0;
        set({
          token: token || "",
          username,
          role,
          clientIds,
          expires_at: exp,
        });
      },

      logout: () =>
        set({ token: "", username: "", role: "", clientIds: [], expires_at: 0 }),
    }),
    {
      name: "iot.auth",
      partialize: (s) => ({
        token: s.token,
        username: s.username,
        role: s.role,
        clientIds: s.clientIds,
        expires_at: s.expires_at,
      }),
      onRehydrateStorage: () => (state) => {
        // Clear expired session before it hits the UI.
        if (state && state.expires_at && state.expires_at < Date.now()) {
          state.token = "";
          state.username = "";
          state.role = "";
          state.clientIds = [];
          state.expires_at = 0;
        }
      },
    }
  )
);

/**
 * Proactive session watcher.
 * Fires 60s before `expires_at` (or immediately if already expired) and clears
 * the auth store, forcing a redirect to /login on the next Protected render.
 *
 * Called once from App.js on mount. Rescheduled on every auth change.
 */
let _timer = null;
export function startSessionWatcher(onExpire) {
  const schedule = () => {
    if (_timer) {
      clearTimeout(_timer);
      _timer = null;
    }
    const { token, expires_at } = useAuth.getState();
    if (!token || !expires_at) return;
    // Trigger 60 seconds before real expiry so the user has time to re-auth.
    const ms = expires_at - Date.now() - 60_000;
    _timer = setTimeout(() => {
      useAuth.getState().logout();
      if (typeof onExpire === "function") onExpire();
    }, Math.max(0, ms));
  };
  schedule();
  return useAuth.subscribe(schedule);
}
